import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Empty, Link, Select, selectOptions, ui } from '../ui';
import { CURATION_DECISIONS } from '@goodbuild/rules/curation';
import { formatDate, formatMoney } from '@goodbuild/rules/format';
import { ApiError, useApi, useApiMutation } from '../api';
import { CreemSync } from './creem-sync';
import { Screen, Table } from './shared';

/**
 * The catalogue.
 *
 * **Everything here is ours** (#144). Under the marketplace model curation was
 * GOODBUILD's and release was the studio's — section 10.1 made release state
 * developer-controlled — and the argument against a create form on this page
 * was that a title could then exist without its studio ever having touched it.
 * There is no studio: we make the games, the date is a decision nobody else is
 * party to, and `Add a title` is the only door there is.
 *
 * What that old argument is still owed is that there is not a *second* way to
 * create one. `POST /v1/admin/games` validates with `titleCreateInput` and
 * writes through `createTitle`, both in `apps/api/src/lib/titles` — and
 * `lib/admin/games.ts` still has no function that creates a title.
 *
 * **The pitches used to be here too, on `/admin/publishing`, and #142 removed
 * them.** See the note at the foot of this file for what went and what was
 * deliberately left in the database.
 *
 * **Editing a title is a page now, not a row that grows** (#136). `Edit` used
 * to expand a panel underneath the row — rename, price, studio, then the eight
 * art slots — inside a `<td colSpan={8}>` of this table. Everything wrong with
 * that followed from where it was: no address for a game, so nothing to link
 * to and nothing to bookmark; the width of a table column for a panel of
 * pictures; and only the fields a *list* carries, so the gameplay link and
 * `updated_at` could not be shown at all. It is `/admin/games/:productId` now
 * and the cell holds a link. See `./admin-game`.
 *
 * What stays here is triage: curation and release are on both screens
 * deliberately, because approving four titles in a row should not be four page
 * loads. Everything about *one* game is over there.
 *
 * So curation here is a decision rather than a state picker. The select offered
 * all five states, including the two a title passes through before anybody has
 * looked at it — an administrator could put a title on sale, or pull one out of
 * review, from a dropdown in a table row. Three buttons instead, the same three
 * the creator queue's shape uses, posting through the same function in core.
 *
 * That queue answers "what needs a decision". This page answers "what do we
 * have", which is the question somebody asks when a game that is already out
 * has a problem — and which no queue can answer, because a queue by definition
 * only holds what has not been decided. It is why the decision lives on both.
 */

/**
 * The release states, as a picker, built once rather than per render.
 *
 * The enum with the underscores taken out for reading. Exported because the
 * title page has the same picker (#136) — one list, so the two screens cannot
 * offer different states for the same column.
 *
 * The currency list went with the price form to `./admin-game`; nothing on this
 * table sets a price any more.
 */
export const RELEASE_STATE_OPTIONS = selectOptions([
  'draft',
  'coming_soon',
  'released',
  'delisted',
]);

/**
 * A title, as `GET /v1/admin/games` returns one.
 *
 * Exported since #136: `GET /v1/admin/games/:productId` answers with exactly
 * this shape for one title, and the page that reads it declaring its own copy
 * is how two screens come to disagree about a nullable field. The shape is
 * `AdminGame` in `apps/api/src/lib/admin/games`, with the dates as the
 * strings JSON made of them.
 */
export interface AdminGame {
  id: string;
  slug: string;
  name: string;
  type: string;
  releaseState: string;
  curationState: string;
  /** The video curation watches, or null where nobody was asked. */
  gameplayUrl: string | null;
  releasedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** The byline. A string since #144 — there is no studio page behind it. */
  developerName: string;
  prices: { currency: string; countryCode: string | null; amountMinor: number }[];
  currentBuild: { id: string; version: string; totalBytes: number } | null;
  /**
   * The store description, on the title page only.
   *
   * Null on every row of the list, which does not read it — a description is
   * long and the list would need one query per title for a column it does not
   * have. See `getGame` in `apps/api/src/lib/admin/games`.
   */
  description: {
    body: string;
    source: string;
    steamAppId: number | null;
    updatedAt: string;
  } | null;
  /** Where else it is sold. Read on the title page; empty on every list row. */
  storeLinks: { platform: string; url: string }[];
}

/** The shared shape of "make a change, then reload the list". */
function useAdminAction(key: readonly unknown[]) {
  const call = useApiMutation();
  const cache = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  /**
   * Returns whether it worked.
   *
   * The forms need to know. A create form that empties itself on the way back
   * from a rejected slug has thrown away what the person typed *and* the reason
   * it was refused, which leaves them retyping a title to find out whether they
   * misread the message.
   */
  async function act(id: string, path: string, init: RequestInit): Promise<boolean> {
    setBusy(id);
    setMessage(null);
    try {
      const result = await call<{ ok: string }>(path, init);
      setMessage(result.ok);
      await cache.invalidateQueries({ queryKey: key });
      // Anything that changes the catalogue can change the overview's counts.
      await cache.invalidateQueries({ queryKey: ['admin', 'overview'] });
      return true;
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : 'That did not work. Try again.');
      return false;
    } finally {
      setBusy(null);
    }
  }

  return { act, message, busy };
}

function Note({ message }: { message: string | null }): React.ReactElement | null {
  if (!message) return null;
  return (
    <p className="gb-body" role="status">
      {message}
    </p>
  );
}

/** A price, or an honest statement that there is not one. */
function priceOf(game: AdminGame): string {
  const money = game.prices.find((entry) => entry.currency === 'EUR') ?? game.prices[0];
  return money ? formatMoney(money.currency as never, money.amountMinor) : 'Not priced';
}

/*
 * `NewTitle` was here, and it is `/admin/games/new` now (#127).
 *
 * It was a panel behind a button: a studio picker, a name, an editable address
 * proposed from it, a price in euros, a creator rate and a video link — six
 * fields, validated with the same `suggestSlug` and `slugProblem` the server
 * checks with, and honest about what it could not do. The note here said it
 * plainly: "a studio's own submission flow resolves collisions and this does
 * not: an administrator who meets a taken address needs a field to change."
 *
 * That sentence is the whole argument for the change. The two doors into the
 * catalogue were not equally good, and the panel was the worse one. There is
 * one door now (#144), and it is the better one: the same flow out of the same
 * parts in `./title-form`, with a Steam import (#128) the panel never had. What
 * stays here is the link.
 */

export function AdminGamesRoute(): React.ReactElement {
  const key = ['admin', 'games'] as const;
  const query = useApi<{ games: AdminGame[] }>(key, '/admin/games');
  /*
    The studios query went with the editor (#136) and the studios went with
    #144. It was here for the two pickers this page used to carry — add a title,
    move a title — and there is nothing to pick: a title's byline is a string it
    carries itself.
  */
  const { act, message, busy } = useAdminAction(key);
  const games = query.data?.games ?? [];

  /**
   * Release is a state and curation is a decision, so they are two calls.
   *
   * Release genuinely is a picker: all four of its states are ours to set, and
   * `coming_soon` after `released` is a real thing somebody does. Curation is
   * not — two of its five states are the ones a title passes through before
   * anybody has looked at it, and the way to keep that true is for this page to
   * have no way of writing them rather than for it to offer them and hope.
   */
  function setRelease(game: AdminGame, value: string): void {
    void act(game.id, `/admin/games/${game.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ releaseState: value }),
    });
  }

  function decideCuration(game: AdminGame, decision: string): void {
    void act(game.id, `/admin/games/${game.id}/curation`, {
      method: 'POST',
      body: JSON.stringify({ decision }),
    });
  }

  const COLUMNS = ['Title', 'Studio', 'Curation', 'Release', 'Price', 'Build', 'Released', ''];

  return (
    <Screen title="Games" aside={`${games.length} in the catalogue`} query={query}>
      <Note message={message} />

      {/*
        The way in to the create, rather than the create itself.

        A link and not a button, because it goes somewhere: `/admin/games/new`
        is a page of its own, and a step-by-step form needs the width and the
        attention that a panel above a table cannot give it. This screen is
        opened many times a day to look something up, and the form is opened
        occasionally — one press of distance is the right trade in that
        direction, which is what the panel it replaces already argued.
      */}
      <p className="gb-body">
        <Link href="/admin/games/new" className={ui.button ?? ''}>
          Add a title
        </Link>{' '}
        <span className={ui.statLabel ?? ''}>
          It arrives as a draft, and approving it is a second, separate press.
        </span>
      </p>

      <CreemSync
        path="/admin/creem/sync"
        hint="Creem takes the money, so it needs its own copy of every title on sale. It carries a title once it is approved, released and priced in euros or dollars. Anything it will not carry is listed here with the reason."
      />

      {games.length === 0 ? (
        <Empty>
          Nothing in the catalogue yet. A title is added from the link above and arrives as a draft
          — approving it is a second, separate press.
        </Empty>
      ) : (
        <Table head={COLUMNS}>
          {games.map((game) => (
            <tr key={game.id}>
              <td>
                <Link href={`/g/${game.slug}`}>{game.name}</Link>
                <br />
                <span className={ui.statLabel ?? ''}>{game.type.replace(/_/g, ' ')}</span>
              </td>
              <td>
                {/*
                  The byline, and no longer a link (#144). It pointed at
                  `/admin/studios/:id` — the studio's people, its build tokens,
                  the address a contract was drawn against — and there is no
                  studio. This is a string on the title itself.
                */}
                {game.developerName}
              </td>
              <td>
                {/*
                  Where the title is, then what can be done about it. The state
                  is read rather than picked — `draft` and `submitted` are the
                  two a title passes through before anybody has looked at it,
                  and neither is a decision — and the button for the state it is
                  already in is disabled rather than hidden, so the row does not
                  reflow every time a decision lands.
                */}
                <span className={ui.statLabel ?? ''}>{game.curationState.replace(/_/g, ' ')}</span>
                <div style={{ display: 'flex', gap: 'var(--gb-space-2)', flexWrap: 'wrap' }}>
                  {CURATION_DECISIONS.map((decision) => (
                    <button
                      key={decision}
                      type="button"
                      className={ui.buttonSecondary ?? ''}
                      disabled={busy === game.id || game.curationState === decision}
                      onClick={() => decideCuration(game, decision)}
                    >
                      {decision.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </td>
              <td>
                <Select
                  options={RELEASE_STATE_OPTIONS}
                  value={game.releaseState}
                  disabled={busy === game.id}
                  onChange={(state) => setRelease(game, state)}
                  aria-label={`Release state for ${game.name}`}
                />
              </td>
              <td className={ui.numeric ?? ''}>{priceOf(game)}</td>
              <td className={ui.numeric ?? ''}>
                {/*
                  What `default` currently serves. "None" rather than a dash,
                  because a title with no build is a title nobody can install
                  and that is worth reading as a fact rather than as a gap.
                */}
                {game.currentBuild ? game.currentBuild.version : 'None'}
              </td>
              <td className={ui.numeric ?? ''}>
                {game.releasedAt ? formatDate(new Date(game.releasedAt)) : '—'}
              </td>
              <td>
                {/*
                  A link, and it goes somewhere (#136).

                  It was a button that expanded a panel inside this row —
                  rename, price, studio and eight art slots, drawn in a
                  `colSpan` cell whose width is decided by the longest studio
                  name in the catalogue. Nothing about that panel had an
                  address: no link to send anybody, nothing to bookmark, and a
                  Back that closed it rather than leaving the title.
                */}
                <Link href={`/admin/games/${game.id}`} className={ui.buttonSecondary ?? ''}>
                  Open
                </Link>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Screen>
  );
}

/*
  `AdminSubmissionsRoute` and its `Submission` shape were here, and #142 removed
  them.

  It was the staff side of the pitch form at `/publishing`: a list of games
  strangers had written in about, with four buttons moving each one between
  reading, talking, accepted and declined. The form is gone, so nothing can add
  a row, and a whole screen whose list can only shrink is one everybody stops
  opening.

  `GET /v1/admin/submissions` went with it, and so — once the review queue was
  removed — did `POST /v1/admin/submissions/:id`, which was the last way to mark
  one answered. It was kept on purpose at the time, because people had written
  in good faith and `/admin/queue` still listed what was waiting; with that
  screen gone there was nothing left to call it.

  The `publishing_submission` table is untouched. What those people actually
  wrote is still there to read, and answering one is an email.
*/
